<script>
import { ref } from 'vue';
import { mapGetters } from 'vuex';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useConversationLabels } from 'dashboard/composables/useConversationLabels';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import Spinner from 'shared/components/Spinner.vue';
import LabelDropdown from 'shared/components/ui/label/LabelDropdown.vue';
import Button from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    Spinner,
    LabelDropdown,
    Button,
  },
  setup() {
    const { isAdmin } = useAdmin();

    const {
      savedLabels,
      activeLabels,
      accountLabels,
      addLabelToConversation,
      removeLabelFromConversation,
    } = useConversationLabels();

    const showSearchDropdownLabel = ref(false);

    const toggleLabels = () => {
      showSearchDropdownLabel.value = !showSearchDropdownLabel.value;
    };

    const closeDropdownLabel = () => {
      showSearchDropdownLabel.value = false;
    };

    const keyboardEvents = {
      KeyL: {
        action: e => {
          e.preventDefault();
          toggleLabels();
        },
      },
      Escape: {
        action: () => {
          if (showSearchDropdownLabel.value) {
            toggleLabels();
          }
        },
        allowOnFocusedInput: true,
      },
    };
    useKeyboardEvents(keyboardEvents);
    return {
      isAdmin,
      savedLabels,
      activeLabels,
      accountLabels,
      addLabelToConversation,
      removeLabelFromConversation,
      showSearchDropdownLabel,
      closeDropdownLabel,
      toggleLabels,
    };
  },
  data() {
    return {
      selectedLabels: [],
    };
  },

  computed: {
    ...mapGetters({
      conversationUiFlags: 'conversationLabels/getUIFlags',
    }),
  },
};
</script>

<template>
  <div class="sidebar-labels-wrap">
    <div
      v-if="!conversationUiFlags.isFetching"
      class="contact-conversation--list"
    >
      <div
        v-on-clickaway="closeDropdownLabel"
        class="label-wrap flex flex-wrap"
        @keyup.esc="closeDropdownLabel"
      >
        <div class="conversation-labels-controls">
          <button
            type="button"
            class="conversation-labels-picker"
            @click="toggleLabels"
          >
            <span class="conversation-labels-picker__placeholder">
              {{ $t('CONTACT_PANEL.LABELS.LABEL_SELECT.TITLE') }}...
            </span>
            <span
              class="conversation-labels-picker__icon i-lucide-chevron-down"
              aria-hidden="true"
            />
          </button>
          <Button
            faded
            xs
            icon="i-lucide-plus"
            class="conversation-labels-add-button"
            :label="
              $t('CONTACT_PANEL.LABELS.CONVERSATION.ADD_BUTTON').split(' ')[0]
            "
            @click="toggleLabels"
          />

          <div
            :class="{
              'block visible': showSearchDropdownLabel,
              'hidden invisible': !showSearchDropdownLabel,
            }"
            class="conversation-labels-dropdown border rounded-lg bg-n-alpha-3 backdrop-blur-[100px] absolute w-full shadow-lg border-n-strong dark:border-n-strong p-2 box-border z-[9999]"
          >
            <LabelDropdown
              v-if="showSearchDropdownLabel"
              :account-labels="accountLabels"
              :selected-labels="savedLabels"
              :allow-creation="isAdmin"
              @add="addLabelToConversation"
              @remove="removeLabelFromConversation"
            />
          </div>
        </div>
        <div class="conversation-labels-active">
          <woot-label
            v-for="label in activeLabels"
            :key="label.id"
            :title="label.title"
            :description="label.description"
            show-close
            :color="label.color"
            variant="smooth"
            class="conversation-label max-w-[calc(100%-0.5rem)]"
            @remove="removeLabelFromConversation"
          />
        </div>
      </div>
    </div>
    <Spinner v-else />
  </div>
</template>

<style lang="scss" scoped>
.sidebar-labels-wrap {
  margin-bottom: 0;
}
.contact-conversation--list {
  width: 100%;
  border-radius: 10px;

  .label-wrap {
    line-height: 1.5rem;
    position: relative;
    align-items: flex-start;
  }
}

.conversation-labels-controls {
  position: relative;
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  align-items: stretch;
  gap: 1.5rem;
}

.conversation-labels-active {
  display: flex;
  min-width: 0;
  flex: 1 1 100%;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem 0;
}

.conversation-labels-dropdown {
  top: calc(100% + 0.5rem);
  left: 0;
}

.conversation-labels-picker {
  display: inline-flex;
  min-width: 0;
  min-height: 2.75rem;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex: 1 1 auto;
  padding: 0 1rem;
  border: 1px solid #dfe3e8;
  border-radius: 0.75rem;
  background: #fff;
  color: #a1a8b0;
  cursor: pointer;
  font-size: 1rem;
  text-align: left;
}

.conversation-labels-picker:hover {
  border-color: #cbd5e1;
}

.conversation-labels-picker__placeholder {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.conversation-labels-picker__icon {
  width: 1.25rem;
  height: 1.25rem;
  flex: 0 0 1.25rem;
  color: #7b8490;
}

.conversation-labels-add-button {
  min-height: 2.75rem;
  flex: 0 0 auto;
  border: 1px solid #dfe3e8 !important;
  border-radius: 0.75rem !important;
  background: #fff !important;
  color: #278c68 !important;
  font-weight: 600 !important;
}

.conversation-label {
  display: inline-flex !important;
  width: max-content;
  max-width: 100%;
  flex: 0 0 auto;
}

@media (max-width: 640px) {
  .conversation-labels-controls {
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .conversation-labels-picker {
    flex: 1 1 100%;
  }

  .conversation-labels-add-button {
    width: 100%;
  }
}
</style>
